import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { Modal, Table, TableColumnConfig, Text } from '@weave/design-system';
import {
  ChecklistMetricCriteria,
  ChecklistMetricStatus,
  MetricItemData,
} from '../../handoff-checklist-v2.types';
import { getStatusIcon } from '../../utils/status-icon';

interface MetricDetailModalProps {
  metric: MetricItemData | null;
  isOpen: boolean;
  onClose: () => void;
}

type MetricDetailModalRow = {
  location?: string;
  criteria?: string;
  status?: ChecklistMetricStatus;
  statusText?: string;
  messageNode?: React.ReactNode;
  criteriaItems?: ChecklistMetricCriteria[];
};

type MetricDetailModalColumn = TableColumnConfig<MetricDetailModalRow> & {
  disableSortBy?: boolean;
};

export const MetricDetailModal = ({
  metric,
  isOpen,
  onClose,
}: MetricDetailModalProps) => {
  if (!isOpen || !metric) return null;

  const isMultiLocation = 'locationResults' in metric;
  const columns: MetricDetailModalColumn[] = isMultiLocation
    ? [
        {
          Header: 'Location',
          accessor: (row) => row,
          id: 'location',
          cellRenderer: (row: MetricDetailModalRow) => (
            <Text size="medium" as="div">
              {row.location}
            </Text>
          ),
          disableSortBy: true,
        },
        {
          Header: 'Result',
          accessor: (row) => row,
          id: 'result',
          cellRenderer: (row: MetricDetailModalRow) => (
            <div css={styles.criteriaList}>
              {row.criteriaItems?.length ? (
                row.criteriaItems.map((criteria, criteriaIndex) =>
                  renderCriterion(criteria, criteriaIndex)
                )
              ) : (
                <Text size="small" color="light">
                  —
                </Text>
              )}
            </div>
          ),
          disableSortBy: true,
        },
      ]
    : [
        {
          Header: 'Criteria',
          accessor: (row) => row,
          id: 'criteria',
          cellRenderer: (row: MetricDetailModalRow) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Text size="medium" as="div">
                {row.criteria}
              </Text>
              {row.statusText && <Text size="small">{row.statusText}</Text>}
              {row.messageNode && (
                <div style={{ marginTop: '4px' }}>{row.messageNode}</div>
              )}
            </div>
          ),
          disableSortBy: true,
        },
        {
          Header: 'Status',
          accessor: (row) => row,
          id: 'status',
          cellRenderer: (row: MetricDetailModalRow) => {
            const StatusIcon = row.status ? getStatusIcon(row.status) : null;
            return StatusIcon ? <StatusIcon css={styles.statusIcon} /> : null;
          },
          disableSortBy: true,
          maxWidth: 120,
        },
      ];

  const data: MetricDetailModalRow[] = isMultiLocation
    ? metric.locationResults.map((location) => ({
        location: location.locationName || location.locationId,
        criteriaItems: location.criterias,
      }))
    : metric.criteria.map((criteria) => ({
        criteria: criteria.description,
        status: criteria.status,
        statusText: criteria.message,
        messageNode: criteria.messageNode,
      }));

  return (
    <Modal show={isOpen} onClose={onClose} maxWidth={600}>
      <Modal.Header onClose={onClose}>{metric.name}</Modal.Header>
      <Modal.Body css={styles.modalBody}>
        <Table
          colConfig={columns}
          data={data}
          hasResponsiveColWidths
          tableStyle={isMultiLocation ? styles.multiLocationTable : styles.table}
        />
      </Modal.Body>
    </Modal>
  );
};

const renderCriterion = (criteria: ChecklistMetricCriteria, criteriaIndex: number) => {
  const StatusIcon = getStatusIcon(criteria.status);
  const resultText =
    criteria.status === ChecklistMetricStatus.PASSED ? 'Passed' : criteria.message || '—';

  return (
    <div key={`${criteria.description}:${criteriaIndex}`} css={styles.criterionContent}>
      {criteria.messageNode ?? (
        <>
          <div css={styles.criterionHeader}>
            {StatusIcon && <StatusIcon css={styles.statusIconSmall} />}
            <Text size="medium" as="span">
              {criteria.description}
            </Text>
          </div>
          {criteria.actualValue !== undefined && (
            <div css={styles.actualValue}>
              <Text size="medium" weight="bold" as="span">
                Actual:{' '}
              </Text>
              <Text size="medium" as="span">
                {criteria.actualValue}
              </Text>
            </div>
          )}
          {resultText !== 'Passed' && criteria.actualValue === undefined && (
            <Text size="small" color="light">
              {resultText ?? '-'}
            </Text>
          )}
        </>
      )}
    </div>
  );
};

const tableStyles = css`
  margin: 0;

  [role='columnheader'] {
    padding-bottom: ${theme.spacing(2)};
    border-bottom: 0;
  }

  [role='cell'] {
    padding-top: ${theme.spacing(3)};
    padding-bottom: ${theme.spacing(3)};
    vertical-align: middle;
    border-bottom: 0 !important;
  }

  [role='row'] {
    border-bottom: 0;
  }
`;

const styles = {
  modalBody: css`
    padding: ${theme.spacing(0, 4, 0, 4)};
  `,
  table: tableStyles,
  multiLocationTable: css`
    ${tableStyles}

    border: 1px solid #d4d9e1;
    border-radius: 8px;
    overflow: hidden;

    [role='columnheader'] {
      background-color: #f4f5f7;
      border-bottom: 1px solid #d4d9e1;
    }

    [role='columnheader']:first-of-type,
    [role='cell']:first-of-type {
      width: 40%;
      border-right: 1px solid #d4d9e1;
    }

    [role='rowgroup'] > div:not(:last-of-type) > [role='row'] {
      border-bottom: 1px solid #d4d9e1;
    }
  `,
  criteriaList: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  criterionContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  criterionHeader: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `,
  statusIconSmall: css`
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  `,
  actualValue: css`
    padding-left: calc(16px + ${theme.spacing(1.5)});
  `,
  statusIcon: css`
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  `,
};
